/**
 * @fileoverview Syndicator Showdown — Firebase Client Initialisation
 * @module services/firebase
 *
 * Single initialisation point for the Firebase client SDK.
 * Import db, auth, googleProvider from here — never call initializeApp() elsewhere.
 *
 * ─── ENVIRONMENT VARIABLES ─────────────────────────────────────────────────────
 *
 * Create web/.env.local (gitignored) with values from Firebase Console:
 * Project Settings → General → Your apps → Web app → SDK setup
 *
 * VITE_FIREBASE_API_KEY=...
 * VITE_FIREBASE_AUTH_DOMAIN=...
 * VITE_FIREBASE_PROJECT_ID=syndicator-showdown
 * VITE_FIREBASE_STORAGE_BUCKET=...
 * VITE_FIREBASE_MESSAGING_SENDER_ID=...
 * VITE_FIREBASE_APP_ID=...
 *
 * ─── FIREBASE CONSOLE SETUP ───────────────────────────────────────────────────
 *
 * Authentication → Sign-in method → Enable:
 * ✓ Google (for players — one-tap sign in)
 * ✓ Email/Password (for operators + direct players)
 *
 * Authentication → Settings → Authorized domains → Add:
 * syndicatorshowdown.com.au
 * syndicatorshowdown.com
 * syndicatorshowdown.au
 * localhost (development)
 */

import { initializeApp }                                        from 'firebase/app';
import { getAuth, GoogleAuthProvider, connectAuthEmulator,
         setPersistence, browserSessionPersistence }           from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator }               from 'firebase/firestore';
import { getFunctions }                                        from 'firebase/functions';
import { getStorage, connectStorageEmulator }                  from 'firebase/storage';
import { getMessaging, isSupported }                           from 'firebase/messaging';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// Initialize Auth with Session Persistence
export const auth = getAuth(app);
setPersistence(auth, browserSessionPersistence).catch(console.warn);
export const db            = getFirestore(app);
export const functions     = getFunctions(app, 'australia-southeast1');
export const storage       = getStorage(app);
export const googleProvider = new GoogleAuthProvider();

// TEMP DIAGNOSTIC (P0 auth regression) — auth.app / db.app must be the same
// app instance (this module is the only initializeApp() call site, so this
// should always be true, but confirms it rather than assuming it).
console.info('[FIREBASE INSTANCES]', {
  appName:      app.name,
  authAppName:  auth.app.name,
  dbAppName:    db.app.name,
  projectId:    firebaseConfig.projectId,
  authDomain:   firebaseConfig.authDomain,
  useEmulators: import.meta.env.VITE_USE_FIREBASE_EMULATORS,
});

/**
 * Local/CI E2E testing only — never true in normal dev or production builds.
 * Set VITE_USE_FIREBASE_EMULATORS=true (see web/.env.e2e) to point this
 * client at the local Auth + Firestore emulators instead of the real
 * syndicator-showdown project. See playwright.config.js.
 */
if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

// Configures Google auth to always show the account picker
googleProvider.setCustomParameters({ prompt: 'select_account' });

/**
 * Firebase Cloud Messaging — only initialised if the browser supports it.
 * Not all browsers do (e.g. Safari on iOS pre-16.4, Firefox on iOS).
 */
export async function getMessagingInstance() {
  const supported = await isSupported();
  return supported ? getMessaging(app) : null;
}

export default app;